import React from 'react';
import { Tag } from 'antd-mobile';
import './MatchTags.css';

/**
 * 紧凑型比赛标签显示组件（用于列表）
 * 只显示标签，不提供管理功能
 * 
 * @param {Object} props
 * @param {Object} props.matchTags - 比赛层面标签数据
 * @param {Object} props.playerTags - 选手层面标签数据（Map结构，key为playerId）
 * @param {String} props.playerId - 当前显示的选手ID（可选，用于只显示该选手的标签）
 */
const MatchTagsCompact = ({ matchTags, playerTags = {}, playerId = null }) => {
  // 标签解释说明
  const tagDescriptions = {
    // 实力对比类
    '爆冷': '低积分方战胜高积分方（积分差≥100且<150）',
    '碾压': '高积分方正常获胜（积分差≥100且<150）',
    '实力悬殊': '双方实力差距悬殊（积分差≥150）',
    '棋逢对手': '双方实力接近（积分差<50）',
    // 比分特征类
    '血战局': '比分非常接近，竞争激烈（所有局分差≤3分，且至少一局≥20分）',
    '碾压局': '一方明显优势（至少一局分差≥10分，且获胜方至少赢2局）',
    '险胜局': '比分非常接近，险胜（至少一局分差=1或2分）',
    '屠杀': '比分差距非常大，一方完全压制（至少一局分差≥15分）',
    // 积分变化类
    '爽局': '实力差距很大，但弱者逆袭获胜，积分暴涨',
    '暴赚': '积分大幅上升，成就感满满（积分增长≥20）',
    '大赚': '积分大幅上升，表现优秀（积分增长≥15且<20）',
    '正常': '积分变化正常，符合预期（积分变化在-5到+15之间）',
    '小赚': '积分小幅上升，略有收获（积分增长>0且<5）',
    '血亏': '实力差距悬殊，强者轻松获胜，但积分可能下降或增长很少',
    '大亏': '积分大幅下降，需要反思（积分下降≥10）'
  };

  // 判断标签类型
  const getTagType = (tag) => {
    const strengthTags = ['爆冷', '碾压', '实力悬殊', '棋逢对手'];
      const scoreTags = ['血战局', '碾压局', '险胜局', '屠杀'];
    
    if (strengthTags.includes(tag)) return 'strength';
    if (scoreTags.includes(tag)) return 'score';
    return 'other';
  };

  // 获取标签颜色（区分实力对比类和比分特征类）
  const getTagColor = (tag, type = 'auto') => {
    if (type === 'auto') {
      const tagType = getTagType(tag);
      
      // 实力对比类 - 使用蓝色系
      if (tagType === 'strength') {
        const strengthColors = {
          '爆冷': '#ff4d4f',      // 红色 - 爆冷
          '碾压': '#1890ff',      // 蓝色 - 碾压
          '实力悬殊': '#722ed1',  // 紫色 - 实力悬殊
          '棋逢对手': '#52c41a'   // 绿色 - 棋逢对手
        };
        return strengthColors[tag] || '#1890ff';
      }
      
      // 比分特征类 - 使用橙色/红色系
      if (tagType === 'score') {
        const scoreColors = {
          '血战局': '#f5222d',    // 深红 - 血战局
          '碾压局': '#fa8c16',    // 橙色 - 碾压局
          '险胜局': '#faad14',    // 黄色 - 险胜局
          '屠杀': '#ff4d4f'     // 红色 - 屠杀
        };
        return scoreColors[tag] || '#fa8c16';
      }
      
      // 积分变化类（选手层面）
      const ratingColors = {
        '爽局': '#ff4d4f',
        '暴赚': '#ff7a45',
        '大赚': '#ffa940',
        '正常': '#52c41a',
        '小赚': '#73d13d',
        '血亏': '#ff4d4f',
        '大亏': '#ff4d4f'
      };
      return ratingColors[tag] || '#108ee9';
    }
    return '#87d068';
  };

  // 渲染比赛层面标签（紧凑版）
  const renderMatchTags = () => {
    if (!matchTags) return null;
    
    const { autoTags = {}, manualTags = [] } = matchTags;
    const { strengthTag, scoreTags = [] } = autoTags;
    const hasTags = strengthTag || scoreTags.length > 0 || manualTags.length > 0;
    
    if (!hasTags) return null;

    return (
      <div className="tags-compact-group">
        {strengthTag && (
          <Tag 
            color={getTagColor(strengthTag, 'auto')}
            className={`tag-auto tag-compact tag-strength`}
            title={tagDescriptions[strengthTag] || strengthTag}
          >
            {strengthTag}
          </Tag>
        )}
        {scoreTags.map((tag, index) => (
          <Tag 
            key={index}
            color={getTagColor(tag, 'auto')}
            className={`tag-auto tag-compact tag-score`}
            title={tagDescriptions[tag] || tag}
          >
            {tag}
          </Tag>
        ))}
        {manualTags.slice(0, 2).map((tagObj, index) => (
          <Tag
            key={index}
            color={getTagColor(tagObj.tag, 'manual')}
            className="tag-manual tag-compact"
          >
            {tagObj.tag}
          </Tag>
        ))}
        {manualTags.length > 2 && (
          <Tag className="tag-compact tag-more">
            +{manualTags.length - 2}
          </Tag>
        )}
      </div>
    );
  };

  // 渲染选手层面标签（紧凑版）
  const renderPlayerTags = (targetPlayerId) => {
    if (!playerTags || !targetPlayerId) return null;
    
    // 处理 playerTags，可能是对象或 Map 结构
    let playerTagsData = playerTags;
    if (playerTagsData instanceof Map) {
      playerTagsData = Object.fromEntries(playerTagsData);
    }
    
    // 尝试多种方式获取 playerId
    const playerIdStr = String(targetPlayerId);
    const playerTagData = playerTagsData[playerIdStr] || 
                          playerTagsData[targetPlayerId] ||
                          (targetPlayerId && playerTagsData[targetPlayerId.toString?.()]);
    
    if (!playerTagData) return null;

    const { autoTags = {}, manualTags = [] } = playerTagData;
    const { ratingTag } = autoTags;
    const hasTags = ratingTag || manualTags.length > 0;
    
    if (!hasTags) return null;

    return (
      <div className="tags-compact-group player-tags-compact">
        {ratingTag && (
          <Tag 
            color={getTagColor(ratingTag, 'auto')}
            className="tag-auto tag-compact"
          >
            {ratingTag}
          </Tag>
        )}
        {manualTags.filter(t => t.isPublic).slice(0, 1).map((tagObj, index) => (
          <Tag
            key={index}
            color={getTagColor(tagObj.tag, 'manual')}
            className="tag-manual tag-compact"
          >
            {tagObj.tag}
          </Tag>
        ))}
      </div>
    );
  };

  // 如果只传入 matchTags，只渲染比赛标签
  if (matchTags && !playerId) {
    return (
      <div className="match-tags-compact">
        {renderMatchTags()}
      </div>
    );
  }
  
  // 如果只传入 playerTags 和 playerId，只渲染个人标签
  if (playerId && !matchTags) {
    return (
      <div className="match-tags-compact">
        {renderPlayerTags(playerId)}
      </div>
    );
  }
  
  // 如果两者都有，都渲染
  return (
    <div className="match-tags-compact">
      {/* 比赛层面标签 */}
      {renderMatchTags()}
      
      {/* 选手层面标签（如果指定了playerId） */}
      {playerId && renderPlayerTags(playerId)}
    </div>
  );
};

export default MatchTagsCompact;
